import { useState, useCallback, useRef, useEffect, useMemo, useSyncExternalStore } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useFormatters } from "@/lib/i18n/useFormatters";
import { getDisplayName, getSenderFromMetadata } from "@/lib/utils/display-name";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import JobChangeSuggestionBanner from "@/components/shared/JobChangeSuggestionBanner";
import { Mail, Phone, MapPin, Calendar, Paperclip, Star, Sparkles, Video, FileText, Layout, Check, X, AlertTriangle, Share2, MessageSquare, Megaphone, Loader2, Rocket, Users, CreditCard, Settings, ExternalLink, ArrowDownLeft, ArrowUpRight, Send, ArrowLeft, RotateCcw, MoreHorizontal, ListPlus, Trash2, Inbox, StickyNote, Download, PlugZap, CheckSquare, Briefcase, Building2, Clock, User as UserIcon, UserCircle2, Activity, Link as LinkIcon } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AddToListDialog from "@/components/shared/AddToListDialog";
import ConfirmDeleteDialog from "@/components/shared/ConfirmDeleteDialog";
import ComposeEmailDialog from "@/components/shared/ComposeEmailDialog";

import { MeetingsTable, EmailsTable, FilesTable, NotesTable, TasksTable } from "@/components/shared/DrillDownTable";
import RecordCallsTab from "@/components/shared/RecordCallsTab";
import type { NoteItem } from "@/components/shared/SheetDrillDown";
import UserAvatar from "@/components/shared/Avatar";
import CompanyLogo from "@/components/shared/CompanyLogo";
import EntityListBadges from "@/components/shared/EntityListBadges";
import MoreFieldsPanel, { MoreFieldRow, type MorePanelField } from "@/components/shared/MoreFieldsPanel";
import { ChevronDown, ChevronRight, List as ListIcon } from "lucide-react";
import { useContactCustomFields } from "@/lib/hooks/use-contact-custom-fields";
import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import { useActiveGridColumnPrefs } from "@/hooks/useActiveGridColumnPrefs";
import { useDebouncedContactUpdate } from "@/lib/hooks/use-debounced-contact-update";
import LinkedInIcon from "@/components/shared/LinkedInIcon";
import EditableDetailField from "@/components/shared/EditableDetailField";
import EditableLocationField from "@/components/shared/EditableLocationField";
import MultiValueDetailField from "@/components/shared/MultiValueDetailField";
import { formatPhone } from "@/lib/format-phone";
import EditableCompany from "@/components/shared/EditableCompany";
import { EmailDrillDown, FileDrillDown, DealDrillDown, NoteDrillDown } from "@/components/shared/SheetDrillDown";
import MeetingIntelligenceSheet from "@/components/shared/MeetingIntelligenceSheet";
import MeetingBriefingSheet from "@/components/shared/MeetingBriefingSheet";
import CompanyDetailSheet from "@/components/shared/CompanyDetailSheet";
import TaskDetailSheet from "@/components/shared/TaskDetailSheet";
import LinkedText from "@/components/shared/LinkedText";
import type { NavigationItem } from "@/components/shared/SheetDrillDown";
import { personEditStore } from "@/lib/utils/person-edit-store";
import { validateLinkedinUrl } from "@/lib/utils/linkedin";
import { cn } from "@/lib/utils";
import { people as allPeople, companies as allCompanies } from "@/data/dummy";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useGetContactLazyQuery, DdbEngagementType, NoteStatusEnum } from "@/lib/graphql/types-and-hooks";
import { useTaskMutations } from "@/lib/hooks/use-task-mutations";
import { useDeleteNote } from "@/lib/hooks/use-delete-note";
import { isLovablePreview } from "@/lib/utils/environment";

import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import { useIsReadOnly } from "@/lib/rbac";
import { useContactMutations } from "@/lib/hooks/use-contact-mutations";
import { resolveSheetDeleteAction } from "@/lib/utils/sheet-delete";
import { sanitizeDisplayDates } from "@/lib/utils/safe-display-dates";
import { useContactUpdate } from "@/lib/hooks/use-contact-update";
import { useContactEngagements } from "@/lib/hooks/use-contact-engagements";
import { useContactFiles } from "@/lib/hooks/use-contact-files";
import { useEntityByName } from "@/lib/hooks/use-entity-by-name";
import { useContactNotes } from "@/lib/hooks/use-contact-notes";
import { useTasks as useDummyTasks } from "@/contexts/TasksContext";
import { useTasks as useBackendTasks } from "@/lib/hooks/use-tasks";
import { useUploadFile } from "@/lib/hooks/use-upload-file";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import NoteEditor from "@/components/shared/NoteEditor";
import type { PartialBlock } from "@blocknote/core";
import CustomerEmailPreview, { type CustomerEmailType } from "@/components/shared/CustomerEmailPreview";
import PersonSheetUiElementsPanel from "@/components/shared/PersonSheetUiElementsPanel";

const locationSuggestions = [...new Set([...allPeople.map(p => p.location).filter(Boolean), ...allCompanies.map(c => c.location).filter(Boolean)])] as string[];

type SummarySection = {
  title: string;
  bullets?: (string | { text: string })[];
  body?: string;
};

// Convert AI summary JSON to BlockNote blocks
function summaryToBlocks(summary: SummarySection[]): PartialBlock[] {
  const blocks: PartialBlock[] = [];

  summary.forEach((section) => {
    if (!section || (!section.bullets && !section.body)) return;

    // Add section title as heading
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: section.title, styles: {} }],
    });

    // Add bullets as bullet list if present
    if (Array.isArray(section.bullets)) {
      section.bullets.forEach((bullet) => {
        const text = typeof bullet === 'string' ? bullet : bullet.text;
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text, styles: {} }],
        });
      });
    }

    // Add body as paragraph if present and no bullets
    if (section.body && !section.bullets) {
      blocks.push({
        type: "paragraph",
        content: [{ type: "text" as const, text: section.body, styles: {} }],
      });
    }
  });

  return blocks;
}

interface Person {
  id: string;
  name: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  location?: string;
  lastEmail?: string;
  lastMeeting?: string;
  nextMeeting?: string;
  createdBy?: string;
}

interface PersonDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: Person | null;
  hideOverlay?: boolean; // Hide overlay when opened as a nested/stacked sheet
  onBack?: () => void; // Show back arrow to return to previous sheet
  onFieldUpdate?: (field: string, value: string) => void; // Notify parent of field changes
  onDelete?: (id: string) => void; // Notify parent when record is deleted
}

const dummyMeetings = [
  { id: "1", title: "Quarterly Business Review", name: "Quarterly Business Review", date: "Feb 27, 2026", duration: "45 min", attendees: ["Doug", "Lisa Park"], people: ["Doug", "Lisa Park"], company: "" },
  { id: "2", title: "Product Roadmap Discussion", name: "Product Roadmap Discussion", date: "Feb 20, 2026", duration: "30 min", attendees: ["Doug"], people: ["Doug"], company: "" },
  { id: "3", title: "Contract Renewal Sync", name: "Contract Renewal Sync", date: "Feb 12, 2026", duration: "30 min", attendees: ["Doug", "Mike Chen"], people: ["Doug", "Mike Chen"], company: "" },
  { id: "4", title: "Technical Deep Dive", name: "Technical Deep Dive", date: "Feb 5, 2026", duration: "60 min", attendees: ["Doug", "Lisa Park"], people: ["Doug", "Lisa Park"], company: "" },
  { id: "5", title: "Initial Discovery Call", name: "Initial Discovery Call", date: "Jan 28, 2026", duration: "30 min", attendees: ["Doug"], people: ["Doug"], company: "" },
  { id: "6", title: "Follow-up on Pricing", name: "Follow-up on Pricing", date: "Jan 20, 2026", duration: "20 min", attendees: ["Doug"], people: ["Doug"], company: "" },
  { id: "7", title: "Intro Meeting", name: "Intro Meeting", date: "Jan 10, 2026", duration: "30 min", attendees: ["Doug", "Lisa Park"], people: ["Doug", "Lisa Park"], company: "" },
];

// Emails are generated dynamically so the person's name replaces "them"
function getPersonEmails(personName: string) {
  return [
    { id: "1", subject: "Re: Migration Timeline Update", date: "Feb 28, 2026", from: personName, to: ["Doug"], cc: ["Lisa Park"], direction: "inbound" as const },
    { id: "2", subject: "Proposal — Q2 Engagement", date: "Feb 25, 2026", from: "Doug", to: [personName], direction: "outbound" as const },
    { id: "3", subject: "Re: Meeting Follow-up", date: "Feb 21, 2026", from: personName, to: ["Doug", "Lisa Park"], direction: "inbound" as const },
    { id: "4", subject: "Meeting Follow-up", date: "Feb 20, 2026", from: "Doug", to: [personName], cc: ["Lisa Park"], direction: "outbound" as const },
    { id: "5", subject: "Introduction", date: "Feb 10, 2026", from: "Doug", to: [personName], direction: "outbound" as const },
  ];
}

const dummyFiles = [
  { id: "1", name: "Q2_Proposal_v2.pdf", size: "2.4 MB", date: "Feb 25, 2026", source: "email" },
  { id: "2", name: "Technical_Spec.docx", size: "890 KB", date: "Feb 20, 2026", source: "email" },
  { id: "3", name: "Meeting_Notes_Feb12.pdf", size: "340 KB", date: "Feb 12, 2026", source: "meeting" },
];

// Edits are persisted via the shared personEditStore (see
// `@/lib/utils/person-edit-store`) so any other surface that renders person
// data (PeoplePage table, MeetingBriefingSheet, drill-down sheets) sees the
// same updates within a session, even when there is no backend round-trip.

/**
 * The Summary tab's ID — a value the Tabs components compare against, never
 * copy (its label comes from `detailSheets.tabSummary`). Named so the i18n lint
 * does not read it as a hardcoded string where it appears inside JSX.
 */
const SUMMARY_TAB_ID = "summary";

export default function PersonDetailSheet({ open, onOpenChange, person, hideOverlay = false, onBack, onFieldUpdate, onDelete }: PersonDetailSheetProps) {
  const workspaceId = useWorkspaceId();

  const [activeTab, setActiveTab] = useState(SUMMARY_TAB_ID);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { deleteContact } = useContactMutations();
  const { deleteTask } = useTaskMutations();
  const { t } = useTranslation();
  const { formatDate, formatDateTime, formatTime } = useFormatters();
  const { deleteNote } = useDeleteNote();
  const { scheduleUpdate: scheduleContactFieldUpdate } = useDebouncedContactUpdate(500);
  const [navStack, setNavStack] = useState<NavigationItem[]>([]);
  const [showUiElements, setShowUiElements] = useState(false);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const favoritesEnabled = useFeatureFlag(FEATURE_FLAGS.FAVORITES);
  const multiValuePhoneEnabled = useFeatureFlag(FEATURE_FLAGS.MULTI_VALUE_PHONE);
  const isReadOnly = useIsReadOnly();
  // The detail-sheet kebab (Add to list / Send Email / Delete) is all
  // mutation actions, so hide it for read-only users.
  const drillDownActionsEnabled = useFeatureFlag(FEATURE_FLAGS.DRILL_DOWN_ACTIONS) && !isReadOnly;
  // HubSpot-synced calls (coffee#4959); hides the tab and skips its query when off.
  const callsTabEnabled = useFeatureFlag(FEATURE_FLAGS.CALLS_TAB);
  const [emailPreviewType, setEmailPreviewType] = useState<CustomerEmailType | null>(null);
  
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);

  // Nested PersonDetailSheet state (for clicking on related people)
  const [nestedPersonSheetOpen, setNestedPersonSheetOpen] = useState(false);
  const [nestedSelectedPerson, setNestedSelectedPerson] = useState<{ id: string; name: string; title?: string; company?: string; email?: string; phone?: string; } | null>(null);

  // CompanyDetailSheet state (stacked sheet)
  const [companySheetOpen, setCompanySheetOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<{ id: string; name: string; industry?: string; description?: string; location?: string; website?: string; domain?: string; employees?: string; revenue?: string; phone?: string; linkedin?: string; lastEmail?: string; lastMeeting?: string; nextMeeting?: string; } | null>(null);

  // TaskDetailSheet state (stacked sheet) — lets the task title open the task
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Meeting/Briefing intelligence sheet state
  const [meetingIntelligenceOpen, setMeetingIntelligenceOpen] = useState(false);
  const [meetingIntelligenceNote, setMeetingIntelligenceNote] = useState<{ id: string; name: string; company: string; people: string[]; date: string } | null>(null);
  const [meetingIntelligenceTab, setMeetingIntelligenceTab] = useState("summary");
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [briefingMeeting, setBriefingMeeting] = useState<{ id: string; title: string; date: string; time: string; duration: string; type: string; attendees: string[]; link?: string } | null>(null);

   // Company state — derive initial value from person's company prop
  const getCompanyDetailsFromPerson = useCallback((p: Person | null) => {
    if (!p?.company) return null;
    if (isLovablePreview()) {
      const matched = allCompanies.find(c => c.name === p.company);
      return {
        id: matched?.id || p.company,
        name: p.company,
        domain: matched?.website?.replace(/^https?:\/\//, '') || null,
        logoUrl: null as string | null,
      };
    }
    // Production: the person prop carries a company *name* only — the real
    // account id arrives with getContact and is synced below. Leave the id
    // empty until then; falling back to the name would drill into an account
    // that resolves to nothing.
    return {
      id: '',
      name: p.company,
      domain: null as string | null,
      logoUrl: null as string | null,
    };
  }, []);

  const [companyDetails, setCompanyDetails] = useState<{
    id: string;
    name: string;
    domain?: string | null;
    logoUrl?: string | null;
  } | null>(() => getCompanyDetailsFromPerson(person));

  // Reset company details when person changes
  useEffect(() => {
    setCompanyDetails(getCompanyDetailsFromPerson(person));
  }, [person?.id, person?.company, getCompanyDetailsFromPerson]);

  // Backend contact update hook
  const {
    updateName,
    updateEmail,
    updateAdditionalEmails,
    updatePhone,
    updateJobTitle,
    updateLinkedIn,
    updateLocation,
    updateCompany,
    flush: flushContactUpdate,
  } = useContactUpdate(person?.id || '');
  // Don't refetch on success - causes flash. Apollo cache handles optimistic updates.

  // Backend data hooks for tabs (only fetch when sheet is open)
  const { engagements: rawMeetings, hasMore: hasMeetingMore, loadMore: loadMoreMeetings } = useContactEngagements({
    contactId: person?.id || '',
    type: DdbEngagementType.Meeting,
    skip: !open,
  });
  const { engagements: rawEmailEngagements, hasMore: hasEmailMore, loadMore: loadMoreEmails } = useContactEngagements({
    contactId: person?.id || '',
    type: DdbEngagementType.Email,
    skip: !open,
  });
  const { files: contactFiles, refetch: refetchFiles } = useContactFiles(person?.id || '', { skip: !open });
  // Resolves attendee / sender display names to real records — backend search
  // in production, dummy people in preview.
  const { resolvePerson } = useEntityByName();
  const { notes: contactNotes, refetch: refetchContactNotes } = useContactNotes(person?.id || '', { skip: !open });
  // Preview mode: dummy tasks scope by person name (see dummy.ts linkedPeople).
  const { tasks: dummyTasks } = useDummyTasks();
  // Production: tasks are notes with type=task; fetch the ones associated with
  // this contact by id (this sheet previously read the dummy context + matched
  // on person name, so real tasks never appeared here). Mirrors DealDetailSheet.
  const personTaskFilter = useMemo(() => ({ contactId: person?.id }), [person?.id]);
  const { tasks: backendPersonTasks } = useBackendTasks({
    filter: personTaskFilter,
    skip: !open || !person?.id,
  });
  const personTasks = useMemo(() => {
    if (isLovablePreview()) {
      if (!person?.name) return [];
      return dummyTasks.filter(t => t.linkedPeople?.includes(person.name));
    }
    return backendPersonTasks.map(t => ({
      id: t.id!,
      title: t.title || 'Untitled',
      dueDate: t.dueDate || '',
      assignee: t.user?.preferredName || t.user?.givenName || '',
      completed: t.status === NoteStatusEnum.Completed,
    }));
  }, [dummyTasks, backendPersonTasks, person?.name, person?.id]);
  const { uploadFile, uploading, uploadProgress } = useUploadFile({
    entityType: 'contact',
    entityId: person?.id || '',
    onSuccess: () => { refetchFiles(); },
  });

  // Transform backend engagements to list format
  const meetings = useMemo(() => {
    const transformed = rawMeetings.map((m: any) => {
      const title = m.displayName || m.description || 'Meeting';
      const attendees = m.contacts?.map((c) => {
        return getDisplayName(c.properties?.firstName, c.properties?.lastName, c.primaryEmail);
      }).filter(Boolean) || [];

      return {
        ...m,
        id: m.id,
        title,
        date: m.startTime
          ? formatDate(m.startTime)
          : '',
        duration: '30 min',
        attendees,
      };
    });

    // Sort by date descending (most recent first)
    const sorted = transformed.sort((a, b) => {
      const dateA = new Date(a.startTime || a.date || 0).getTime();
      const dateB = new Date(b.startTime || b.date || 0).getTime();
      return dateB - dateA;
    });

    return sorted;
  }, [rawMeetings, open]);

  const emailEngagements = useMemo(() => {
    const transformed = rawEmailEngagements.map((e: any) => {
      const subject = e.title || e.displayName || e.description || 'Email';
      // Use metadataFrom (the actual email "From" header) for the sender,
      // matching the reference implementation (coffee-ui). Falls back to contacts[0] for preview mode.
      const from = getSenderFromMetadata(e.metadataFrom);

      return {
        ...e,  // Spread first so our computed fields override
        id: e.id,
        subject,
        displayName: subject,  // Override displayName with subject
        date: e.startTime || '',
        from,
        senders: [from],
        direction: 'inbound' as const,
      };
    });

    // Sort by date descending (most recent first)
    const sorted = transformed.sort((a, b) => {
      const dateA = new Date(a.startTime || a.date || 0).getTime();
      const dateB = new Date(b.startTime || b.date || 0).getTime();
      return dateB - dateA;
    });

    return sorted;
  }, [rawEmailEngagements, open]);


  const handleAvatarClick = () => {
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      setShowUiElements(prev => !prev);
      setActiveTab(prev => prev === "ui-elements" ? "summary" : "ui-elements");
    } else {
      clickTimerRef.current = setTimeout(() => { clickCountRef.current = 0; }, 500);
    }
  };

  // Editable overrides
  const [overrides, setOverrides] = useState<Partial<Person>>(() =>
    person?.id ? { ...(personEditStore.get(person.id) || {}) } : {}
  );
  const [emails, setEmails] = useState<string[]>([]);
  const [emailsEdited, setEmailsEdited] = useState(false); // Track if user has manually edited emails
  // Multi-value phones (feature-flagged; UI-only until backend supports additional phones)
  const [phones, setPhones] = useState<string[]>([]);
  const [phonesEdited, setPhonesEdited] = useState(false);

  // Re-hydrate overrides from the cache when the displayed person changes,
  // so edits made earlier are preserved across reopens / nested drill-downs.
  useEffect(() => {
    setOverrides(person?.id ? { ...(personEditStore.get(person.id) || {}) } : {});
    setEmailsEdited(false);
    setPhonesEdited(false);
  }, [person?.id]);

  // Hydrate the emails list from any prior edits made in the grid (or elsewhere)
  // so a second email added upstream is visible when the sheet reopens.
  useEffect(() => {
    if (!person?.id) return;
    const stored = personEditStore.get(person.id) as any;
    if (Array.isArray(stored?.emails) && stored.emails.length > 0) {
      setEmails(stored.emails.filter((e: unknown): e is string => !!e));
    } else if (person.email) {
      setEmails([person.email]);
    } else {
      setEmails([]);
    }
  }, [person?.id, person?.email]);

  // Hydrate the phones list from any prior edits in the grid or elsewhere,
  // so additional numbers added upstream appear when the sheet reopens.
  useEffect(() => {
    if (!person?.id) return;
    const stored = personEditStore.get(person.id) as any;
    if (Array.isArray(stored?.phones) && stored.phones.length > 0) {
      setPhones(stored.phones.filter((p: unknown): p is string => !!p));
    } else if (person.phone) {
      setPhones([formatPhone(person.phone) || person.phone]);
    } else {
      setPhones([]);
    }
  }, [person?.id, person?.phone]);

  // Subscribe to the cross-component person store so edits made in the parent
  // grid (or any other surface) while this sheet is open are immediately
  // reflected here. Merge non-destructively over local overrides so an
  // in-progress edit in this sheet isn't clobbered by the same store update
  // it just wrote.
  const storePersonOverride = useSyncExternalStore(
    personEditStore.subscribe,
    () => (person?.id ? personEditStore.get(person.id) : undefined),
    () => (person?.id ? personEditStore.get(person.id) : undefined),
  );
  useEffect(() => {
    if (!person?.id) return;
    setOverrides({ ...(storePersonOverride || {}) });
    // Also mirror emails changes coming from the store while the sheet is open,
    // as long as the user hasn't started editing them in place.
    const storedEmails = (storePersonOverride as any)?.emails;
    if (!emailsEdited && Array.isArray(storedEmails)) {
      setEmails(storedEmails.filter((e: unknown): e is string => !!e));
    }
    const storedPhones = (storePersonOverride as any)?.phones;
    if (!phonesEdited && Array.isArray(storedPhones)) {
      setPhones(storedPhones.filter((p: unknown): p is string => !!p));
    }
  }, [storePersonOverride, person?.id, emailsEdited, phonesEdited]);

  useEffect(() => {
    if (!open) {
      setEmailsEdited(false);
      setPhonesEdited(false);
    }
  }, [open]);

  // Backend data hooks
  const [getContact, { data: contactData, loading: contactLoading }] = useGetContactLazyQuery({
    fetchPolicy: 'no-cache',
  });

  // Fetch contact details when sheet opens with a backend ID
  useEffect(() => {
    if (open && person?.id && !isLovablePreview()) {
      getContact({
        variables: {
          id: person.id,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getContact ref can be unstable; open + person.id are sufficient
  }, [open, person?.id]);

  // Transform backend data to Person format. The lazy query keeps the
  // previous contact's response in `data` while a fetch for the newly
  // selected person is in flight, so gate on id — otherwise fast switching
  // renders the old person's fields under the new selection.
  const backendContact = contactData?.contact && contactData.contact.id === person?.id ? contactData.contact : undefined;
  const backendPerson = useMemo((): Person | null => {
    if (!backendContact) return null;

    const displayName = getDisplayName(backendContact.properties?.firstName, backendContact.properties?.lastName, backendContact.primaryEmail);

    const account = backendContact.accounts?.edges?.[0]?.node;

    return {
      id: backendContact.id,
      name: displayName,
      email: backendContact.primaryEmail || undefined,
      title: backendContact.properties?.jobTitle || undefined,
      company: account?.properties?.name || undefined,
      phone: backendContact.properties?.phone || undefined,
      location: [backendContact.properties?.city, backendContact.properties?.state, backendContact.properties?.country]
        .filter(Boolean).join(', ') || undefined,
      linkedin: backendContact.properties?.linkedinProfileUrl || undefined,
      lastEmail: backendContact.lastEmail?.startTime
        ? formatDate(backendContact.lastEmail.startTime)
        : undefined,
      lastMeeting: backendContact.lastEngagement?.startTime
        ? formatDate(backendContact.lastEngagement.startTime)
        : undefined,
      nextMeeting: backendContact.nextEngagement?.startTime
        ? formatDate(backendContact.nextEngagement.startTime)
        : undefined,
    };
  }, [backendContact]);

  // Parse AI summary JSON into BlockNote blocks (must be before early return)
  const summaryBlocks = useMemo(() => {
    if (isLovablePreview()) {
      return null;
    }

    if (!backendContact?.properties?.aiSummary) {
      return null;
    }

    try {
      let rawContent = backendContact.properties.aiSummary;

      // If it's a string, strip markdown code blocks (```json ... ```)
      if (typeof rawContent === 'string') {
        rawContent = rawContent.trim();
        // Remove markdown code block wrapper if present
        const codeBlockMatch = rawContent.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/);
        if (codeBlockMatch) {
          rawContent = codeBlockMatch[1].trim();
        }
      }

      // Parse the JSON
      const parsed = typeof rawContent === 'string'
        ? JSON.parse(rawContent)
        : rawContent;

      // Check if it's an array of sections
      if (Array.isArray(parsed)) {
        return summaryToBlocks(parsed as SummarySection[]);
      }

      // Could also be { sections: [...] }
      if (parsed?.sections && Array.isArray(parsed.sections)) {
        return summaryToBlocks(parsed.sections as SummarySection[]);
      }

      // New format: { relationshipStatus, recentContext, keyTakeaways }
      if (parsed?.relationshipStatus || parsed?.recentContext || parsed?.keyTakeaways) {
        const blocks: PartialBlock[] = [];

        if (parsed.relationshipStatus) {
          blocks.push({
            type: "heading",
            props: { level: 3 },
            content: [{ type: "text" as const, text: "Relationship Status", styles: {} }],
          });
          blocks.push({
            type: "paragraph",
            content: [{ type: "text" as const, text: parsed.relationshipStatus, styles: {} }],
          });
        }

        if (parsed.recentContext) {
          blocks.push({
            type: "heading",
            props: { level: 3 },
            content: [{ type: "text" as const, text: "Recent Context", styles: {} }],
          });
          blocks.push({
            type: "paragraph",
            content: [{ type: "text" as const, text: parsed.recentContext, styles: {} }],
          });
        }

        if (parsed.keyTakeaways) {
          blocks.push({
            type: "heading",
            props: { level: 3 },
            content: [{ type: "text" as const, text: "Key Takeaways", styles: {} }],
          });
          blocks.push({
            type: "paragraph",
            content: [{ type: "text" as const, text: parsed.keyTakeaways, styles: {} }],
          });
        }

        return blocks;
      }

      return null;
    } catch (e) {
      return null;
    }
  }, [backendContact]);

  // Sync emails from backend data (primaryEmail + additionalEmails)
  // Only sync when not manually edited to avoid overwriting user changes
  useEffect(() => {
    if (backendContact && !emailsEdited && person) {
      const emailList: string[] = [];
      if (backendContact.primaryEmail) {
        emailList.push(backendContact.primaryEmail);
      }
      if (backendContact.properties?.additionalEmails) {
        emailList.push(...backendContact.properties.additionalEmails.filter((e): e is string => !!e));
      }
      setEmails(emailList);
    }
  }, [backendContact?.primaryEmail, backendContact?.properties?.additionalEmails, emailsEdited, person]);

  // Sync company details from backend data (only in non-preview mode)
  useEffect(() => {
    if (!isLovablePreview() && backendContact) {
      const account = backendContact.accounts?.edges?.[0]?.node;
      if (account) {
        setCompanyDetails({
          id: account.id,
          name: account.properties?.name || account.domain || 'Unknown',
          domain: account.domain,
          logoUrl: account.properties?.logoUrl,
        });
      } else {
        setCompanyDetails(null);
      }
    }
  }, [backendContact]);

  // "More" panel: every remaining standard + custom field for the contact.
  // Must be called before any early return to keep hook order stable.
  const { customFields: contactCustomFields } = useContactCustomFields();
  const [moreOpen, setMoreOpen] = useState(false);
  const customOverridesKey = person?.id ? `more-fields-custom:${person.id}` : "";
  const [customOverrides, setCustomOverrides] = useState<Record<string, string | string[] | boolean>>({});
  useEffect(() => {
    if (!customOverridesKey) return;
    try {
      const raw = localStorage.getItem(customOverridesKey);
      setCustomOverrides(raw ? JSON.parse(raw) : {});
    } catch { setCustomOverrides({}); }
  }, [customOverridesKey]);
  const persistCustomOverrides = (next: Record<string, string | string[] | boolean>) => {
    setCustomOverrides(next);
    if (customOverridesKey) {
      try { localStorage.setItem(customOverridesKey, JSON.stringify(next)); } catch { /* ignore */ }
    }
  };
  // Read column visibility state (shared with the People grid via workspace-scoped
  // localStorage). This lets us mirror the grid's visible columns on the drill-down
  // card, and push hidden columns into the "More fields" panel.
  // Defaults mirror PeoplePage `columns` and `defaultHiddenPeopleColumns`
  // so a fresh user's drill-down order matches the grid.
  const DEFAULT_HIDDEN_PEOPLE_COLUMNS = useMemo(
    () => ["firstName", "lastName", "source", "createdDate"],
    [],
  );
  const DEFAULT_PEOPLE_COLUMN_ORDER = useMemo(
    () => [
      "firstName", "lastName", "title", "company", "email", "createdBy",
      "createdDate", "phone", "location", "linkedin", "lastEmail",
      "lastMeeting", "nextMeeting", "lastStep", "nextStep", "source",
    ],
    [],
  );
  // Mirror the column layout the user actually sees on the People grid
  // (personal layer wins over the workspace default — see useActiveGridColumnPrefs).
  const colsState = useActiveGridColumnPrefs("people_v3");
  const hiddenColKeys = useMemo(
    () => new Set<string>(colsState?.hiddenKeys ?? DEFAULT_HIDDEN_PEOPLE_COLUMNS),
    [colsState?.hiddenKeys, DEFAULT_HIDDEN_PEOPLE_COLUMNS],
  );
  const columnOrder = useMemo(
    () => (colsState?.columnOrder && colsState.columnOrder.length > 0
      ? colsState.columnOrder
      : DEFAULT_PEOPLE_COLUMN_ORDER),
    [colsState?.columnOrder, DEFAULT_PEOPLE_COLUMN_ORDER],
  );

  // Descriptor for standard columns → MorePanelField shape (used when a standard
  // column is hidden in the grid, so it appears in the "More fields" panel).
  const standardFieldDescriptor = useCallback((key: string): MorePanelField | null => {
    const props = (backendContact?.properties ?? {}) as Record<string, any>;
    // Prefer in-session overrides so edits round-trip immediately in the
    // "More fields" panel (e.g. First/Last Name are derived from `name`).
    const effName = (overrides.name ?? person?.name ?? "") as string;
    const nameParts = effName.split(" ");
    const eff = <K extends keyof typeof person>(k: K) =>
      (overrides as any)[k] ?? (person as any)?.[k] ?? "";
    switch (key) {
      case "firstName": return { key: "firstName", label: t("fields.people.firstName"), type: "text", value: nameParts[0] ?? "", icon: UserIcon };
      case "lastName": return { key: "lastName", label: t("fields.people.lastName"), type: "text", value: nameParts.slice(1).join(" "), icon: UserIcon };
      case "title": return { key: "title", label: t("fields.people.title"), type: "text", value: eff("title"), icon: Briefcase };
      case "company": return { key: "company", label: t("detailSheets.account"), type: "text", value: eff("company"), readOnly: true, icon: Building2 };
      case "email": return { key: "email", label: t("fields.people.email"), type: "email", value: eff("email"), icon: Mail };
      case "phone": return { key: "phone", label: t("fields.people.phone"), type: "phone", value: eff("phone"), icon: Phone };
      case "location": return { key: "location", label: t("fields.people.location"), type: "location", value: eff("location"), icon: MapPin };
      case "linkedin": return { key: "linkedin", label: t("fields.people.linkedin"), type: "url", value: eff("linkedin"), icon: LinkedInIcon };
      case "createdBy": return { key: "createdBy", label: t("fields.people.createdBy"), type: "text", value: person?.createdBy ?? "", readOnly: true, icon: UserCircle2 };
      case "createdDate": return { key: "createdDate", label: t("fields.people.createdDate"), type: "date", value: (backendContact as any)?.createTs ? formatDate((backendContact as any).createTs) : (person as any)?.createdDate, readOnly: true, icon: Calendar };
      case "lastEmail": return { key: "lastEmail", label: t("fields.people.lastEmail"), type: "text", value: person?.lastEmail ?? "", readOnly: true, icon: Mail };
      case "lastMeeting": return { key: "lastMeeting", label: t("fields.people.lastMeeting"), type: "text", value: person?.lastMeeting ?? "", readOnly: true, icon: Video };
      case "nextMeeting": return { key: "nextMeeting", label: t("fields.people.nextMeeting"), type: "text", value: person?.nextMeeting ?? "", readOnly: true, icon: Video };
      case "lastStep": return { key: "lastStep", label: t("fields.people.lastStep"), type: "text", value: (overrides as any).lastStep ?? props.lastActivityText ?? (person as any)?.lastStep ?? "", icon: Activity };
      case "nextStep": return { key: "nextStep", label: t("fields.people.nextStep"), type: "text", value: (overrides as any).nextStep ?? props.nextActivityText ?? (person as any)?.nextStep ?? "", icon: Activity };
      case "source": return { key: "source", label: t("fields.people.source"), type: "text", value: props.source ?? (person as any)?.source, readOnly: true, icon: LinkIcon };
      default: return null;
    }
  }, [backendContact, person, overrides]);

  // "More fields" panel content = hidden standard columns + hidden custom columns.
  const moreFields = useMemo<MorePanelField[]>(() => {
    const custom = (backendContact?.customProperties ?? {}) as Record<string, any>;
    const std: MorePanelField[] = [];
    for (const key of hiddenColKeys) {
      if (key === "name") continue;
      const d = standardFieldDescriptor(key);
      if (d) std.push(d);
    }
    // Build a lookup of every known custom field descriptor. Include any
    // persisted grid-column customColumns as a fallback so custom fields that
    // aren't in the backend/preview registry (e.g. legacy locally-added
    // columns) still surface in the "More fields" panel when hidden from the
    // grid — matching what the Manage Columns menu shows.
    const seenCustomKeys = new Set<string>();
    const allCustom: Array<{ key: string; label: string; type: string; selectOptions?: string[]; crmName?: string }> = [];
    for (const cf of contactCustomFields) {
      seenCustomKeys.add(cf.key);
      allCustom.push(cf);
    }
    for (const cc of colsState?.customColumns ?? []) {
      if (!cc.key.startsWith("custom_") || seenCustomKeys.has(cc.key)) continue;
      seenCustomKeys.add(cc.key);
      allCustom.push({
        key: cc.key,
        label: cc.label,
        type: cc.type || "text",
        selectOptions: cc.selectOptions,
      });
    }
    const customRows: MorePanelField[] = allCustom
      .filter(cf => hiddenColKeys.has(cf.key))
      .map(cf => {
        const override = customOverrides[cf.key];
        const backendVal = cf.crmName ? custom[cf.crmName] : undefined;
        // Prefer the shared personEditStore (grid + detail write here) so a
        // value entered in the grid appears in the detail card and vice versa.
        const storeVal = (overrides as any)?.[cf.key];
        // Prefer the shared store even when it's an empty string — clearing
        // a multi-select in the grid writes "" and must win over any stale
        // localStorage override so the detail card reflects the cleared state.
        const value =
          storeVal !== undefined
            ? storeVal
            : override !== undefined
              ? override
              : (backendVal ?? "");
        return {
          key: cf.key,
          label: cf.label,
          type: cf.type,
          options: cf.selectOptions,
          value: value as any,
          isCustom: true,
        };
      });
    return [...std, ...customRows];
  }, [backendContact, contactCustomFields, hiddenColKeys, customOverrides, overrides, standardFieldDescriptor, colsState?.customColumns]);

  const handleMoreFieldChange = (key: string, value: string | boolean | string[]) => {
    // Custom fields → persist to localStorage overrides.
    if (key.startsWith("custom_")) {
      persistCustomOverrides({ ...customOverrides, [key]: value });
      // Also mirror into the shared personEditStore + notify the grid so the
      // value round-trips both ways (grid ↔ detail card).
      const strVal = Array.isArray(value) ? value.join(", ") : String(value);
      if (person?.id) personEditStore.merge(person.id, { [key]: strVal } as any);
      // Grid pages persist via onFieldUpdate → updateCell → debounced save.
      // At mounts without that wiring (Today, global search, meeting sheets)
      // save directly, or the edit would never reach the backend.
      if (!onFieldUpdate && !isLovablePreview() && person?.id) {
        scheduleContactFieldUpdate(person.id, key, strVal);
      }
      onFieldUpdate?.(key, strVal);
      return;
    }
    // Standard fields → route through the same updateField path used by primary rows.
    const strVal = Array.isArray(value) ? value.join(", ") : String(value);
    switch (key) {
      case "firstName": {
        const parts = (current.name ?? "").split(" ");
        parts[0] = strVal;
        updateField("name", parts.join(" ").trim());
        break;
      }
      case "lastName": {
        const first = (current.name ?? "").split(" ")[0] || "";
        updateField("name", `${first} ${strVal}`.trim());
        break;
      }
      case "title":
      case "email":
      case "phone":
      case "location":
      case "linkedin":
        updateField(key as keyof Person, strVal);
        break;
      case "lastStep":
      case "nextStep":
        // Preview-only: persist to overrides so the value round-trips within the session.
        setOverrides(prev => {
          const next = { ...prev, [key]: strVal } as Partial<Person>;
          if (person?.id) personEditStore.merge(person.id, { [key]: strVal } as any);
          return next;
        });
        onFieldUpdate?.(key, strVal);
        break;
      default:
        break;
    }
  };

  if (!person) return null;

  // Use backend data when available, otherwise fall back to prop data
  const useBackendData = !isLovablePreview() && backendPerson;
  const basePerson = useBackendData ? backendPerson : person;

  const currentEmails = emails.length > 0 ? emails : (basePerson.email ? [basePerson.email] : []);
  // sanitizeDisplayDates guards against a raw Engagement object reaching JSX (React #31)
  // if a caller passes an unformatted contact in the pre-backend-fetch window.
  const current = sanitizeDisplayDates({ ...basePerson, ...overrides });
  const currentPhones = phones.length > 0
    ? phones
    : (current.phone ? [formatPhone(current.phone) || current.phone] : []);
  const dummyEmails = getPersonEmails(current.name);

  const favorited = isFavorite(person.id, "person");

  const handleToggleFavorite = () => {
    toggleFavorite({
      id: person.id,
      type: "person",
      name: current.name,
      subtitle: `${current.title || ""}${current.company ? ` · ${current.company}` : ""}`,
    });
  };

  const updateField = (field: keyof Person, value: string) => {
    // Optimistic update for UI
    setOverrides(prev => {
      const next = { ...prev, [field]: value };
      if (person?.id) personEditStore.merge(person.id, { [field]: value } as any);
      return next;
    });
    // Notify parent of field change (e.g., PeoplePage updates its local state)
    onFieldUpdate?.(field, value);

    // Update backend based on field
    switch (field) {
      case 'name':
        updateName(value);
        break;
      case 'email':
        updateEmail(value);
        break;
      case 'phone':
        updatePhone(value);
        break;
      case 'title':
        updateJobTitle(value);
        break;
      case 'linkedin':
        updateLinkedIn(value);
        break;
      case 'location': {
        // Parse location string back to components
        const parts = value.split(',').map(s => s.trim());
        updateLocation({
          city: parts[0] || undefined,
          state: parts[1] || undefined,
          country: parts[2] || undefined,
        });
        break;
      }
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      flushContactUpdate(); // Ensure pending edits are saved before closing
      setNavStack([]);
    }
    onOpenChange(open);
  };

  const pushNav = (item: NavigationItem) => setNavStack(prev => [...prev, item]);
  const popNav = () => setNavStack(prev => prev.slice(0, -1));

  const currentNav = navStack.length > 0 ? navStack[navStack.length - 1] : null;

  // Navigate to company when clicking company name in header
  const handleCompanyClick = () => {
    if (!companyDetails) return;
    // Production drills down by the real account id synced from getContact; if
    // it hasn't landed yet there is nothing to open (and no dummy to consult).
    if (!isLovablePreview()) {
      if (!companyDetails.id) return;
      setSelectedCompany({
        id: companyDetails.id,
        name: companyDetails.name,
        domain: companyDetails.domain ?? undefined,
      });
      setCompanySheetOpen(true);
      return;
    }
    // Preview: hydrate the full record from dummy data.
    const fullCompany = allCompanies.find(c => c.name === companyDetails.name || c.id === companyDetails.id);
    const derivedDomain = companyDetails.domain
      ?? fullCompany?.website?.replace(/^https?:\/\//, '').replace(/\/$/, '')
      ?? undefined;
    setSelectedCompany(fullCompany ? {
      id: fullCompany.id,
      name: fullCompany.name,
      industry: fullCompany.industry,
      description: fullCompany.description,
      location: fullCompany.location,
      website: fullCompany.website,
      domain: derivedDomain,
      employees: fullCompany.employees,
      revenue: fullCompany.revenue,
      phone: (fullCompany as any).phone,
      linkedin: (fullCompany as any).linkedin,
      lastEmail: (fullCompany as any).lastEmail,
      lastMeeting: (fullCompany as any).lastMeeting,
      nextMeeting: (fullCompany as any).nextMeeting,
    } : {
      id: companyDetails.id,
      name: companyDetails.name,
      domain: derivedDomain,
    });
    setCompanySheetOpen(true);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent expandable hideOverlay={hideOverlay} className="sm:max-w-[50vw] w-full overflow-y-auto p-0">
        <VisuallyHidden.Root>
          <SheetTitle>{t("detailSheets.personDetails")}</SheetTitle>
        </VisuallyHidden.Root>
        {currentNav ? (
          <div className="px-6 pt-6 pb-6">
            {currentNav.type === "email" && <EmailDrillDown email={currentNav.data} onBack={popNav} />}
            {currentNav.type === "file" && <FileDrillDown file={currentNav.data} onBack={popNav} />}
            {currentNav.type === "company" && (() => {
              // Open CompanyDetailSheet instead of inline drill-down
              setSelectedCompany(currentNav.data);
              setCompanySheetOpen(true);
              popNav(); // Remove from nav stack
              return null;
            })()}
            {currentNav.type === "person" && (() => {
              // Open nested PersonDetailSheet instead of inline drill-down
              setNestedSelectedPerson(currentNav.data);
              setNestedPersonSheetOpen(true);
              popNav(); // Clear nav stack since we're using stacked sheet
              return null;
            })()}
            {currentNav.type === "meeting" && (() => {
              // Open MeetingIntelligenceSheet or MeetingBriefingSheet instead of drill-down
              const meetingDate = new Date(currentNav.data.date || currentNav.data.startTime);
              const isUpcoming = meetingDate > new Date();
              if (isUpcoming) {
                setBriefingMeeting({
                  id: currentNav.data.id,
                  title: currentNav.data.title || currentNav.data.name || t("detailSheets.meeting"),
                  date: formatDate(meetingDate),
                  time: formatTime(meetingDate),
                  duration: currentNav.data.duration || t("detailSheets.defaultDuration"),
                  type: t("detailSheets.meeting"),
                  attendees: currentNav.data.attendees || [],
                  link: undefined,
                });
                setBriefingOpen(true);
              } else {
                setMeetingIntelligenceNote({
                  id: currentNav.data.id,
                  name: currentNav.data.title || currentNav.data.name || t("detailSheets.meeting"),
                  date: currentNav.data.date || currentNav.data.startTime,
                  people: currentNav.data.attendees || [],
                  company: person?.company || '',
                });
                setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                setMeetingIntelligenceOpen(true);
              }
              popNav();
              return null;
            })()}
            {currentNav.type === "briefing" && (() => {
              // Open MeetingBriefingSheet instead of drill-down
              const meetingData = currentNav.data;
              setBriefingMeeting({
                id: meetingData.id,
                title: meetingData.title || meetingData.name || t("detailSheets.meeting"),
                date: meetingData.date,
                time: meetingData.time || '',
                duration: meetingData.duration || t("detailSheets.defaultDuration"),
                type: t("detailSheets.meeting"),
                attendees: meetingData.attendees || [],
                link: undefined,
              });
              setBriefingOpen(true);
              popNav();
              return null;
            })()}
            {currentNav.type === "deal" && <DealDrillDown deal={currentNav.data} onBack={popNav} onNavigate={pushNav} />}
            {currentNav.type === "note" && <NoteDrillDown note={currentNav.data} onBack={popNav} />}
          </div>
        ) : contactLoading && !isLovablePreview() ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Header + Contact Info */}
            <div className="px-6 pt-6 pb-4 border-b border-border">
              {onBack && (
                <button onClick={() => { flushContactUpdate(); onBack(); }} className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground mb-3 transition">
                  <ArrowLeft className="h-4 w-4" /> {t("detailSheets.back")}
                </button>
              )}
              <div className="flex items-start gap-4">
                <div onClick={handleAvatarClick} className="cursor-default">
                  <UserAvatar
                    name={current.name}
                    src={useBackendData ? backendContact?.profileImageUrl || backendContact?.properties?.profileImageUrl : undefined}
                    size="lg"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <SheetHeader className="p-0 space-y-0">
                    <div className="flex items-center gap-2">
                      <EditableDetailField
                        value={current.name}
                        onChange={v => updateField("name", v)}
                        icon={<span />}
                        className="text-lg font-semibold w-auto min-w-0 shrink [&_span]:text-lg [&_span]:font-semibold [&_input]:text-lg [&_input]:font-semibold"
                      />
                      {favoritesEnabled && (
                        <button onClick={handleToggleFavorite} className="shrink-0 hover:scale-110 transition">
                          <Star className={cn("h-3.5 w-3.5", favorited ? "fill-accent text-accent" : "text-muted-foreground")} />
                        </button>
                      )}
                      {drillDownActionsEnabled && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="shrink-0 hover:bg-accent rounded p-1 transition">
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => setAddToListOpen(true)}>
                              <ListPlus className="h-4 w-4 mr-2" /> {t("common.addToList")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setComposeEmailOpen(true)}>
                              <Mail className="h-4 w-4 mr-2" /> {t("common.sendEmail")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setConfirmDeleteOpen(true)} className="text-destructive focus:text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" /> {t("common.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </SheetHeader>
                </div>
              </div>

              {/* Job-change question about this contact (SC-13586), if one is open. */}
              <JobChangeSuggestionBanner contactId={person?.id} sheetOpen={open} />

              {/* Editable contact details */}
              <div className="mt-4 space-y-0.5">
                {(() => {
                  // Primary field renderers for the standard columns. Keys not in
                  // this map fall through to the shared MoreFieldRow renderer so
                  // custom fields (and any less common standard fields) still render.
                  const primary: Record<string, JSX.Element> = {
                    firstName: (
                      <FieldRow icon={UserIcon} label={t("fields.people.firstName")}>
                        <EditableDetailField
                          hideIcon
                          value={(current.name || "").split(" ")[0] || ""}
                          onChange={v => {
                            const parts = (current.name || "").split(" ");
                            parts[0] = v;
                            updateField("name", parts.join(" ").trim());
                          }}
                          icon={null}
                          placeholder={t("detailSheets.setFirstNamePlaceholder")}
                        />
                      </FieldRow>
                    ),
                    lastName: (
                      <FieldRow icon={UserIcon} label={t("fields.people.lastName")}>
                        <EditableDetailField
                          hideIcon
                          value={(current.name || "").split(" ").slice(1).join(" ")}
                          onChange={v => {
                            const first = (current.name || "").split(" ")[0] || "";
                            updateField("name", `${first} ${v}`.trim());
                          }}
                          icon={null}
                          placeholder={t("detailSheets.setLastNamePlaceholder")}
                        />
                      </FieldRow>
                    ),
                    title: (
                      <FieldRow icon={Briefcase} label={t("fields.people.title")}>
                        <EditableDetailField hideIcon value={current.title || ""} onChange={v => updateField("title", v)} icon={null} placeholder={t("detailSheets.setTitlePlaceholder")} />
                      </FieldRow>
                    ),
                    company: (
                      <FieldRow icon={Building2} label={t("detailSheets.account")}>
                        <EditableCompany
                          value={companyDetails}
                          onChange={(company) => {
                            setCompanyDetails(company);
                            updateCompany(company.id);
                            if (person?.id) {
                              personEditStore.merge(person.id, {
                                company: company.name,
                                companyId: company.id,
                                companyDomain: (company as any).domain || undefined,
                              } as any);
                            }
                            onFieldUpdate?.("company", company.name);
                          }}
                          // Not clickable until there's a real account id to open
                          // (production resolves it from getContact).
                          onDrillDown={
                            companyDetails && (isLovablePreview() || companyDetails.id)
                              ? handleCompanyClick
                              : undefined
                          }
                        />
                      </FieldRow>
                    ),
                    email: (
                      <FieldRow icon={Mail} label={t("fields.people.email")}>
                        <MultiValueDetailField
                          hideIcon
                          values={currentEmails}
                          onValueClick={() => setComposeEmailOpen(true)}
                          onChange={async (emailList) => {
                            setEmailsEdited(true);
                            setEmails(emailList);
                            const primary = emailList[0] || "";
                            if (person?.id) {
                              personEditStore.merge(person.id, {
                                emails: emailList,
                                email: primary,
                              } as any);
                            }
                            onFieldUpdate?.("email", primary);
                            await updateAdditionalEmails(
                              emailList,
                              backendContact?.primaryEmail,
                              backendContact?.properties?.additionalEmails?.filter((e): e is string => !!e) || null
                            );
                          }}
                          icon={null}
                          isLink={false}
                          placeholder={t("detailSheets.addEmailPlaceholder")}
                          validate={(email) => {
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (!emailRegex.test(email)) return "Please enter a valid email address";
                            return null;
                          }}
                        />
                      </FieldRow>
                    ),
                    phone: (
                      <FieldRow icon={Phone} label={t("fields.people.phone")}>
                        {multiValuePhoneEnabled ? (
                          <MultiValueDetailField
                            hideIcon
                            values={currentPhones}
                            onChange={(next) => {
                              const normalized = next.map(v => formatPhone(v) || v);
                              setPhonesEdited(true);
                              setPhones(normalized);
                              if (person?.id) {
                                personEditStore.merge(person.id, { phones: normalized } as any);
                              }
                              updateField("phone", normalized[0] || "");
                            }}
                            icon={null}
                            placeholder={t("detailSheets.addPhonePlaceholder")}
                            validate={(phone) => {
                              const phoneRegex = /^[\d\s()+.-]{10,}$/;
                              if (phone && !phoneRegex.test(phone)) return "Please enter a valid phone number";
                              return null;
                            }}
                          />
                        ) : (
                          <EditableDetailField
                            hideIcon
                            value={formatPhone(current.phone) || current.phone || ""}
                            onChange={v => updateField("phone", formatPhone(v) || v)}
                            icon={null}
                            placeholder={t("detailSheets.setPhonePlaceholder")}
                            validate={(phone) => {
                              const phoneRegex = /^[\d\s()+.-]{10,}$/;
                              if (phone && !phoneRegex.test(phone)) return "Please enter a valid phone number";
                              return null;
                            }}
                          />
                        )}
                      </FieldRow>
                    ),
                    location: (
                      <FieldRow icon={MapPin} label={t("fields.people.location")}>
                        <EditableLocationField compact hideIcon value={current.location || ""} onChange={v => updateField("location", v)} />
                      </FieldRow>
                    ),
                    linkedin: (
                      <FieldRow icon={LinkedInIcon} label="LinkedIn">
                        <EditableDetailField
                          hideIcon
                          value={current.linkedin || ""}
                          onChange={v => updateField("linkedin", v)}
                          icon={null}
                          isLink
                          linkPrefix="https://"
                          placeholder={t("detailSheets.setLinkedinPlaceholder")}
                          validate={(url) => validateLinkedinUrl(url)}
                        />
                      </FieldRow>
                    ),
                    createdBy: (
                      <FieldRow icon={UserCircle2} label={t("fields.people.createdBy")}>
                        <span className="text-xs text-foreground truncate">{current.createdBy || <span className="text-muted-foreground/60">—</span>}</span>
                      </FieldRow>
                    ),
                    createdDate: (
                      <FieldRow icon={Calendar} label={t("fields.people.createdDate")}>
                        <span className="text-xs text-foreground truncate">
                          {(() => {
                            const v = (backendContact as any)?.createTs
                              ? formatDate((backendContact as any).createTs)
                              : (current as any)?.createdDate;
                            return v && v !== "-" ? v : <span className="text-muted-foreground/60">—</span>;
                          })()}
                        </span>
                      </FieldRow>
                    ),
                    lastEmail: (
                      <FieldRow icon={Clock} label={t("fields.people.lastEmail")}>
                        {current.lastEmail && current.lastEmail !== "-" ? (
                          <button
                            onClick={() => {
                              if (useBackendData && backendContact?.lastEmail) {
                                const emailDate = backendContact.lastEmail.startTime
                                  ? formatDate(backendContact.lastEmail.startTime)
                                  : '';
                                pushNav({
                                  type: "email",
                                  data: {
                                    id: backendContact.lastEmail.id,
                                    subject: backendContact.lastEmail.displayName || 'Email',
                                    date: emailDate,
                                    from: person?.name || '',
                                    direction: 'inbound' as const,
                                  }
                                });
                              } else {
                                pushNav({ type: "email", data: dummyEmails[0] });
                              }
                            }}
                            className="text-sm text-foreground hover:text-accent hover:underline transition cursor-pointer text-left truncate"
                          >
                            {current.lastEmail}
                          </button>
                        ) : <span className="text-sm text-muted-foreground/60">—</span>}
                      </FieldRow>
                    ),
                    lastMeeting: (
                      <FieldRow icon={Calendar} label={t("fields.people.lastMeeting")}>
                        {current.lastMeeting && current.lastMeeting !== "-" ? (
                          <button
                            onClick={() => {
                              if (useBackendData && backendContact?.lastEngagement) {
                                setMeetingIntelligenceNote({
                                  id: backendContact.lastEngagement.id,
                                  name: backendContact.lastEngagement.displayName || 'Meeting',
                                  date: backendContact.lastEngagement.startTime || '',
                                  people: [person?.name || ''],
                                  company: person?.company || '',
                                });
                                setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                                setMeetingIntelligenceOpen(true);
                              } else {
                                setMeetingIntelligenceNote({
                                  id: dummyMeetings[0]?.id || '1',
                                  name: dummyMeetings[0]?.title || 'Meeting',
                                  date: current.lastMeeting || '',
                                  people: dummyMeetings[0]?.attendees || [current.name],
                                  company: current.company || '',
                                });
                                setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                                setMeetingIntelligenceOpen(true);
                              }
                            }}
                            className="text-sm text-foreground hover:text-accent hover:underline transition cursor-pointer text-left truncate"
                          >
                            {current.lastMeeting}
                          </button>
                        ) : <span className="text-sm text-muted-foreground/60">—</span>}
                      </FieldRow>
                    ),
                    nextMeeting: (
                      <FieldRow icon={Calendar} label={t("fields.people.nextMeeting")}>
                        {current.nextMeeting && current.nextMeeting !== "-" ? (
                          <button
                            onClick={() => {
                              if (useBackendData && backendContact?.nextEngagement) {
                                const engagement = backendContact.nextEngagement;
                                const meetingDate = engagement.startTime ? new Date(engagement.startTime) : new Date();
                                setBriefingMeeting({
                                  id: engagement.id,
                                  title: engagement.displayName || 'Upcoming Meeting',
                                  date: formatDate(meetingDate),
                                  time: formatTime(meetingDate),
                                  duration: '30 min',
                                  type: t("detailSheets.meeting"),
                                  attendees: [person?.name || ''],
                                  link: undefined,
                                });
                                setBriefingOpen(true);
                              } else {
                                setBriefingMeeting({
                                  id: 'upcoming-1',
                                  title: 'Upcoming Meeting',
                                  date: current.nextMeeting || '',
                                  time: '10:00 AM',
                                  duration: '30 min',
                                  type: 'Google Meet',
                                  attendees: [current.name, 'Doug'],
                                  link: undefined,
                                });
                                setBriefingOpen(true);
                              }
                            }}
                            className="text-sm text-accent font-medium hover:underline transition cursor-pointer text-left truncate"
                          >
                            {current.nextMeeting}
                          </button>
                        ) : <span className="text-sm text-muted-foreground/60">—</span>}
                      </FieldRow>
                    ),
                    lastStep: (
                      <FieldRow icon={Activity} label={t("fields.people.lastStep")}>
                        <EditableDetailField
                          hideIcon
                          value={String((overrides as any).lastStep ?? (backendContact?.properties as any)?.lastActivityText ?? (current as any).lastStep ?? "")}
                          onChange={v => handleMoreFieldChange("lastStep", v)}
                          icon={null}
                          placeholder={t("detailSheets.setLastStepPlaceholder")}
                        />
                      </FieldRow>
                    ),
                    nextStep: (
                      <FieldRow icon={Activity} label={t("fields.people.nextStep")}>
                        <EditableDetailField
                          hideIcon
                          value={String((overrides as any).nextStep ?? (backendContact?.properties as any)?.nextActivityText ?? (current as any).nextStep ?? "")}
                          onChange={v => handleMoreFieldChange("nextStep", v)}
                          icon={null}
                          placeholder={t("detailSheets.setNextStepPlaceholder")}
                        />
                      </FieldRow>
                    ),
                    source: (
                      <FieldRow icon={LinkIcon} label={t("fields.people.source")}>
                        <span className="text-xs text-foreground truncate">
                          {(() => {
                            const v = (backendContact?.properties as any)?.source ?? (current as any).source;
                            return v && v !== "-" ? String(v) : <span className="text-muted-foreground/60">—</span>;
                          })()}
                        </span>
                      </FieldRow>
                    ),
                  };
                  const customByKey = new Map(contactCustomFields.map(cf => [cf.key, cf]));
                  const customValues = (backendContact?.customProperties ?? {}) as Record<string, any>;
                  const seen = new Set<string>();
                  const rows: JSX.Element[] = [];
                  const emit = (key: string) => {
                    if (seen.has(key) || key === "name" || hiddenColKeys.has(key)) return;
                    seen.add(key);
                    if (primary[key]) {
                      rows.push(<div key={key}>{primary[key]}</div>);
                      return;
                    }
                    const cf = customByKey.get(key);
                    if (cf) {
                      const override = customOverrides[cf.key];
                      const backendVal = customValues[cf.crmName];
                      const storeVal = (overrides as any)?.[cf.key];
                      const value =
                        storeVal !== undefined
                          ? storeVal
                          : override !== undefined
                            ? override
                            : (backendVal ?? "");
                      rows.push(
                        <MoreFieldRow
                          key={key}
                          field={{
                            key: cf.key,
                            label: cf.label,
                            type: cf.type,
                            options: cf.selectOptions,
                            value: value as any,
                            isCustom: true,
                          }}
                          onChange={handleMoreFieldChange}
                        />
                      );
                    }
                  };
                  // Iterate strictly in grid column order so drill-down field
                  // ordering matches the grid. Custom fields are appended in
                  // their defined order if not already in the persisted order.
                  for (const k of columnOrder) emit(k);
                  for (const cf of contactCustomFields) emit(cf.key);
                  return rows;
                })()}
                <FieldRow icon={ListIcon} label={t("detailSheets.lists")}>
                  <EntityListBadges type="person" id={current.id} compact />
                </FieldRow>
                <button
                  type="button"
                  onClick={() => setMoreOpen(v => !v)}
                  aria-expanded={moreOpen}
                  className="mt-1 inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition"
                >
                  {moreOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                  <span>{moreOpen ? t("detailSheets.fewerFields") : t("detailSheets.moreFields")}</span>
                </button>
                <MoreFieldsPanel
                  open={moreOpen}
                  onClose={() => setMoreOpen(false)}
                  entity="people"
                  fields={moreFields}
                  onChange={handleMoreFieldChange}
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 pb-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className={cn("w-full mb-5",
                  `grid ${["grid-cols-6", "grid-cols-7", "grid-cols-8"][Number(showUiElements) + Number(callsTabEnabled)]}`
                )}>
                  <TabsTrigger value="summary" className="text-[11px] gap-1"><Sparkles className="h-3 w-3" />{t("detailSheets.tabSummary")}</TabsTrigger>
                  <TabsTrigger value="notes" className="text-[11px] gap-1"><StickyNote className="h-3 w-3" />{t("common.notes")}</TabsTrigger>
                  <TabsTrigger value="tasks" className="text-[11px] gap-1"><CheckSquare className="h-3 w-3" />{t("tasks.title")}</TabsTrigger>
                  <TabsTrigger value="meetings" className="text-[11px] gap-1"><Video className="h-3 w-3" />{t("detailSheets.tabMeetings")}</TabsTrigger>
                  <TabsTrigger value="emails" className="text-[11px] gap-1"><Mail className="h-3 w-3" />{t("detailSheets.tabEmails")}</TabsTrigger>
                  {callsTabEnabled && <TabsTrigger value="calls" className="text-[11px] gap-1"><Phone className="h-3 w-3" />{t("detailSheets.tabCalls")}</TabsTrigger>}
                  <TabsTrigger value="files" className="text-[11px] gap-1"><FileText className="h-3 w-3" />{t("detailSheets.tabFiles")}</TabsTrigger>
                  {showUiElements && <TabsTrigger value="ui-elements" className="text-[11px] gap-1"><Layout className="h-3 w-3" />UI</TabsTrigger>}
                </TabsList>

                {/* Summary */}
                <TabsContent value="summary" className="space-y-4">
                  {summaryBlocks ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground px-4">
                        <Sparkles className="h-3 w-3 text-accent" />
                        <span>{t("detailSheets.aiGeneratedSummary")}</span>
                      </div>
                      <NoteEditor
                        initialContent={summaryBlocks}
                        onChange={() => {}}
                        autoFocus={false}
                        editable={false}
                      />
                      {backendContact?.asOfTs && (
                        <p className="text-[10px] text-muted-foreground pt-1 px-4">
                          {t("detailSheets.lastUpdatedAt", { timestamp: formatDateTime(backendContact.asOfTs) })}
                        </p>
                      )}
                    </div>
                   ) : (
                     <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                       <div className="flex items-center gap-2 text-xs text-muted-foreground">
                         <Sparkles className="h-3 w-3 text-accent" />
                         <span>{t("detailSheets.aiGeneratedSummary")}</span>
                       </div>
                       {(() => {
                         const meetingCount = (isLovablePreview() ? dummyMeetings : meetings).length;
                         const emailCount = (isLovablePreview() ? dummyEmails : emailEngagements).length;
                         if (meetingCount === 0 && emailCount === 0) {
                           return (
                             <p className="text-muted-foreground text-xs italic">
                               {t("detailSheets.noActivityYet", { name: person?.name || t("detailSheets.thisContact") })}
                             </p>
                           );
                         }
                         return isLovablePreview() ? (
                         <div className="space-y-2 text-sm leading-relaxed">
                          <p>
                            <LinkedText
                              text={`${person?.name}${person?.title ? `, ${person.title}` : ""}${person?.company ? ` at ${person.company}` : ""}, is an engaged contact with a consistent communication history.`}
                              onPersonClick={(p) => pushNav({ type: "person", data: { id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone } })}
onCompanyClick={(c) => pushNav({ type: "company", data: { id: c.id, name: c.name, industry: c.industry, description: c.description, location: c.location, website: c.website, employees: c.employees, revenue: c.revenue } as any })}
                            />
                          </p>
                          <p>
                            <LinkedText
                              // eslint-disable-next-line coffee-i18n/no-hardcoded-plural -- Lovable-preview dummy narrative; never rendered in production.
                              text={`You've had ${dummyMeetings.length} meetings and ${dummyEmails.length} email exchanges. The latest email on ${dummyEmails[0]?.date} was regarding "${dummyEmails[0]?.subject}."${current.nextMeeting && current.nextMeeting !== "-" ? ` Your next meeting is scheduled for ${current.nextMeeting}.` : " There are no upcoming meetings scheduled — consider reaching out to maintain momentum."}`}
                              onPersonClick={(p) => pushNav({ type: "person", data: { id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone } })}
                              onCompanyClick={(c) => pushNav({ type: "company", data: { id: c.id, name: c.name, industry: c.industry, description: c.description, location: c.location, website: c.website, employees: c.employees, revenue: c.revenue } as any })}
                            />
                          </p>
                          <p>
                            <Trans
                              i18nKey="detailSheets.personSentimentLine"
                              values={{ firstName: person?.name?.split(" ")[0] }}
                              components={{ positive: <span className="text-emerald-500 font-medium" /> }}
                            />
                          </p>
                         </div>
                         ) : (
                           <p className="text-muted-foreground text-xs italic">
                             {t("detailSheets.noAiSummary")}
                           </p>
                         );
                       })()}
                       <p className="text-[10px] text-muted-foreground pt-1">{t("detailSheets.summaryLastUpdated")}</p>
                     </div>
                   )}
                </TabsContent>

                {/* Notes */}
                <TabsContent value="notes">
                  <NotesTable
                    notes={contactNotes.map(n => ({ id: n.id, name: n.title, date: n.date, type: n.type, content: n.content }))}
                    onRowClick={n => pushNav({ type: "note", data: n as NoteItem })}
                    onAdd={() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: { type: "note", association: person ? { id: person.id, type: "person", name: person.name } : undefined } }))}
                    onDelete={n => deleteNote(n.id, refetchContactNotes)}
                  />
                </TabsContent>

                {/* Tasks */}
                <TabsContent value="tasks">
                  <TasksTable
                    tasks={personTasks.map(t => ({ id: t.id, title: t.title, dueDate: t.dueDate, assignee: t.assignee, completed: t.completed }))}
                    onRowClick={t => setSelectedTaskId(t.id)}
                    onAdd={() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: { type: "task", association: person ? { id: person.id, type: "person", name: person.name } : undefined } }))}
                    onDelete={t => { if (!isLovablePreview()) deleteTask(t.id); }}
                  />
                </TabsContent>

                {/* Meetings */}
                <TabsContent value="meetings">
                  <MeetingsTable
                    meetings={(isLovablePreview() ? dummyMeetings : meetings)}
                    onRowClick={(m) => {
                      const meetingDate = new Date((m as any).startTime || m.date);
                      const isUpcoming = meetingDate > new Date();

                      if (isUpcoming) {
                        setBriefingMeeting({
                          id: m.id,
                          title: (m as any).displayName || (m as any).name || m.title || 'Meeting',
                          date: formatDate(meetingDate),
                          time: formatTime(meetingDate),
                          duration: '30 min',
                          type: t("detailSheets.meeting"),
                          attendees: (m as any).contacts?.map((c: any) => [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail).filter(Boolean) || m.attendees || [person?.name || ''],
                          link: undefined,
                        });
                        setBriefingOpen(true);
                      } else {
                        setMeetingIntelligenceNote({
                          id: m.id,
                          name: (m as any).displayName || (m as any).name || m.title || 'Meeting',
                          date: (m as any).startTime || m.date,
                          people: (m as any).contacts?.map((c: any) => [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail).filter(Boolean) || m.attendees || [person?.name || ''],
                          company: (m as any).accounts?.[0]?.properties?.name || (m as any).accounts?.[0]?.domain || person?.company || '',
                        });
                        setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                        setMeetingIntelligenceOpen(true);
                      }
                    }}
                    onAttendeeClick={async (name) => {
                      // Search backend meeting contacts first (by name match against contact properties)
                      const allMeetings = isLovablePreview() ? dummyMeetings : meetings;
                      for (const m of allMeetings) {
                        const contact = (m as any).contacts?.find((c: any) => {
                          const fullName = `${c?.properties?.firstName || ''} ${c?.properties?.lastName || ''}`.trim();
                          return fullName === name || c?.primaryEmail === name;
                        });
                        if (contact) {
                          const accountName = contact.accounts?.edges?.[0]?.node?.properties?.name || (contact as any).accounts?.[0]?.properties?.name || '';
                          setNestedSelectedPerson({
                            id: contact.id,
                            name: `${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim() || contact.primaryEmail || name,
                            title: contact.properties?.jobTitle || '',
                            company: accountName,
                            email: contact.primaryEmail || '',
                            phone: contact.properties?.phone || '',
                          });
                          setNestedPersonSheetOpen(true);
                          return;
                        }
                      }
                      // Attendee isn't among the loaded meetings' contacts:
                      // resolve the name to a real record (backend search in
                      // production, dummy people in preview).
                      const p = await resolvePerson(name);
                      if (p) {
                        setNestedSelectedPerson({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone });
                        setNestedPersonSheetOpen(true);
                      }
                    }}
                  />
                  {!isLovablePreview() && hasMeetingMore && (
                    <div className="flex justify-center py-4">
                      <Button onClick={loadMoreMeetings} variant="outline" size="sm">
                        {t("detailSheets.loadMore")}
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Emails */}
                <TabsContent value="emails">
                  <EmailsTable
                    emails={(isLovablePreview() ? dummyEmails : emailEngagements)}
                    onRowClick={(e) => {
                      pushNav({
                        type: "email",
                        data: {
                          id: e.id,
                          subject: e.subject || (e as any).displayName || 'Email',
                          date: e.date || (e as any).startTime,
                          from: e.from || 'Unknown',
                          direction: e.direction || 'inbound',
                        },
                      });
                    }}
                    onSenderClick={async (name) => {
                      const p = await resolvePerson(name);
                      if (p) {
                        setNestedSelectedPerson({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone });
                        setNestedPersonSheetOpen(true);
                      }
                    }}
                  />
                  {!isLovablePreview() && hasEmailMore && (
                    <div className="flex justify-center py-4">
                      <Button onClick={loadMoreEmails} variant="outline" size="sm">
                        {t("detailSheets.loadMore")}
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Calls */}
                {callsTabEnabled && (
                  <TabsContent value="calls">
                    <RecordCallsTab recordType="contact" recordId={person?.id || ''} enabled={open && callsTabEnabled} />
                  </TabsContent>
                )}

                {/* Files */}
                <TabsContent value="files">
                  <FilesTable
                    files={isLovablePreview() ? dummyFiles : contactFiles}
                    onRowClick={f => pushNav({ type: "file", data: f })}
                    onFileUpload={uploadFile}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                  />
                </TabsContent>

                {/* Hidden UI-elements gallery (triple-click the avatar).
                    Internal design-system preview, not product copy — it lives
                    in its own file so the i18n lint can exempt it wholesale. */}
                {showUiElements && (
                  <PersonSheetUiElementsPanel onPreviewEmail={setEmailPreviewType} />
                )}
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>

    {/* Customer Email Preview */}
    <CustomerEmailPreview
      open={emailPreviewType !== null}
      onOpenChange={(open) => { if (!open) setEmailPreviewType(null); }}
      emailType={emailPreviewType || "welcome"}
    />


    {/* Meeting Intelligence Sheet - stacked on top */}
    <MeetingIntelligenceSheet
      open={meetingIntelligenceOpen}
      onOpenChange={setMeetingIntelligenceOpen}
      note={meetingIntelligenceNote}
      defaultTab={meetingIntelligenceTab}
      hideOverlay={true}
      onBack={() => setMeetingIntelligenceOpen(false)}
    />

    {/* Meeting Briefing Sheet - stacked on top */}
    <MeetingBriefingSheet
      open={briefingOpen}
      onOpenChange={setBriefingOpen}
      meeting={briefingMeeting}
      isRecording={false}
      onRecordingChange={() => {}}
      hideOverlay={true}
      onBack={() => setBriefingOpen(false)}
    />

    {/* Nested PersonDetailSheet - for clicking on related people */}
    <PersonDetailSheet
      open={nestedPersonSheetOpen}
      onOpenChange={setNestedPersonSheetOpen}
      person={nestedSelectedPerson}
      onBack={() => setNestedPersonSheetOpen(false)}
    />

    {/* CompanyDetailSheet - stacked on top */}
    <CompanyDetailSheet
      open={companySheetOpen}
      onOpenChange={setCompanySheetOpen}
      company={selectedCompany}
      hideOverlay={true}
      onBack={() => setCompanySheetOpen(false)}
    />

    {/* TaskDetailSheet - stacked on top */}
    <TaskDetailSheet
      taskId={selectedTaskId}
      open={!!selectedTaskId}
      onOpenChange={(o) => { if (!o) setSelectedTaskId(null); }}
      hideOverlay={true}
      onBack={() => setSelectedTaskId(null)}
    />

    <AddToListDialog
      open={addToListOpen}
      onOpenChange={setAddToListOpen}
      count={1}
      entityLabel="people"
      recordIds={person ? [person.id] : []}
      onConfirm={(listName) => toast.success(t("detailSheets.addedToList", { name: current.name, list: listName }))}
    />
    <ConfirmDeleteDialog
      open={confirmDeleteOpen}
      onOpenChange={setConfirmDeleteOpen}
      title={`Delete ${current.name}?`}
      description={t("detailSheets.deleteContactDescription")}
      onConfirm={async () => {
        // Parent owns the delete on its home page (People); everywhere else
        // (Deals, Notes, Today, global nav, stacked sheets) the sheet deletes
        // itself so the button isn't a silent no-op. deleteContact handles
        // RBAC, toast, and cache; it throws on failure (already toasted).
        const action = resolveSheetDeleteAction({
          id: person?.id,
          hasParentHandler: !!onDelete,
          isPreview: isLovablePreview(),
        });
        if (action === 'parent') {
          onDelete!(person!.id);
        } else if (action === 'self') {
          try {
            await deleteContact(person!.id);
          } catch {
            /* already toasted by the hook */
          }
        }
        onOpenChange(false);
      }}
    />
    <ComposeEmailDialog
      open={composeEmailOpen}
      onOpenChange={setComposeEmailOpen}
      recipients={current.email ? [{ id: person?.id || '', name: current.name, email: current.email, company: current.company, title: current.title }] : []}
    />
    </>
  );
}

function FieldRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 items-start py-1 [&_.text-sm]:text-xs">
      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-0.5">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="relative min-w-0 flex items-center text-xs">{children}</div>
    </div>
  );
}
